import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Zap, Info, Moon, Sun, X, RefreshCw } from "lucide-react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import ToolsNav from "../components/ToolsNav";
import QuickConvert from "../components/QuickConvert";
import { RenderCMSContent } from "../components/CMSRenderer";
import { convertImage } from "../utils/imageConverters";
import { AdContainer, ResponsiveAd, StickyMobileAd, DesktopOnlyAd, StickySidebar } from "../components/AdPlacement";

export default function ImageConverterTemplate({
  sourceFormat,
  targetFormat,
  acceptTypes,
  slug,
  gradientColors = "from-blue-500 to-cyan-600",
  accentColor = "blue",
  showQualitySlider = false,
  embedded = false,
}) {
  const [images, setImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [quality, setQuality] = useState([90]);
  const [pageContent, setPageContent] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  const updateSEOTags = (seo, fallbackTitle) => {
    document.title = seo?.meta_title || fallbackTitle || `Convert ${sourceFormat} to ${targetFormat} - KayTool`;
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', seo?.meta_description || `Free online ${sourceFormat} to ${targetFormat} converter.`);
  };

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pages/slug/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setPageContent(data);
          updateSEOTags(data.seo, data.title);
        }
      } catch (error) {
        updateSEOTags(null, `Convert ${sourceFormat} to ${targetFormat} - KayTool`);
      }
    };
    if (!embedded) fetchPageContent();
  }, [embedded, slug]);

  const toggleDarkMode = () => setDarkMode(prev => !prev);
  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(file => {
      const ext = file.name.toLowerCase().split('.').pop();
      const acceptExts = acceptTypes.split(',').map(t => t.trim().replace('.', '').replace('image/', ''));
      return acceptExts.some(ae => ext === ae || file.type.includes(ae));
    });
    if (files.length > 0) processMultipleImages(files);
    else toast.error(`Please drop ${sourceFormat} files only`);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) processMultipleImages(files);
    e.target.value = '';
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) imageFiles.push(new File([file], `pasted-image-${Date.now()}.${sourceFormat.toLowerCase()}`, { type: file.type }));
      }
    }
    if (imageFiles.length > 0) {
      e.preventDefault();
      processMultipleImages(imageFiles);
      toast.success(`${imageFiles.length} image${imageFiles.length > 1 ? 's' : ''} pasted!`);
    }
  };

  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  const processMultipleImages = async (files) => {
    setIsProcessing(true);
    setCompressionProgress(0);
    const newImages = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setCompressionProgress(Math.round(((i) / files.length) * 100));
      try {
        const result = await convertImage(file, targetFormat.toLowerCase(), { quality: quality[0] / 100 });
        const convertedFile = new File([result.blob], file.name.replace(/\.[^/.]+$/, result.extension), { type: result.blob.type });
        
        newImages.push({
          id: Date.now() + i,
          name: file.name,
          originalFile: file,
          originalSize: file.size,
          dimensions: { width: result.width, height: result.height },
          convertedFile: convertedFile,
          convertedSize: convertedFile.size,
          preview: URL.createObjectURL(convertedFile),
          originalPreview: URL.createObjectURL(file),
        });
      } catch (error) {
        console.error(`Error converting ${file.name}:`, error);
        toast.error(`Failed to convert ${file.name}`);
      }
    }
    setImages(prev => [...prev, ...newImages]);
    setCompressionProgress(100);
    setIsProcessing(false);
    if (newImages.length > 0) toast.success(`${newImages.length} image${newImages.length > 1 ? 's' : ''} converted to ${targetFormat}!`);
  };

  const downloadImage = (image) => {
    const link = document.createElement("a");
    link.href = image.preview;
    link.download = image.convertedFile.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAllAsZip = async () => {
    if (images.length === 0) return;
    const zip = new JSZip();
    for (const image of images) {
      const response = await fetch(image.preview);
      const blob = await response.blob();
      zip.file(image.convertedFile.name, blob);
    }
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, `converted-${targetFormat.toLowerCase()}-images.zip`);
    toast.success("Downloaded all images as ZIP!");
  };

  const removeImage = (id) => setImages(prev => prev.filter(img => img.id !== id));
  const clearAll = () => { setImages([]); toast.success("All images cleared"); };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (embedded) return <div className="p-4"><p className="text-center text-slate-500">Convert {sourceFormat} to {targetFormat} Tool</p></div>;

  const colorClasses = {
    blue: { bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800', text: 'text-blue-600 dark:text-blue-400', btn: 'bg-blue-600 hover:bg-blue-700' },
    green: { bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-800', text: 'text-green-600 dark:text-green-400', btn: 'bg-green-600 hover:bg-green-700' },
    purple: { bg: 'bg-purple-50 dark:bg-purple-900/20', border: 'border-purple-200 dark:border-purple-800', text: 'text-purple-600 dark:text-purple-400', btn: 'bg-purple-600 hover:bg-purple-700' },
    orange: { bg: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-200 dark:border-orange-800', text: 'text-orange-600 dark:text-orange-400', btn: 'bg-orange-600 hover:bg-orange-700' },
    cyan: { bg: 'bg-cyan-50 dark:bg-cyan-900/20', border: 'border-cyan-200 dark:border-cyan-800', text: 'text-cyan-600 dark:text-cyan-400', btn: 'bg-cyan-600 hover:bg-cyan-700' },
    pink: { bg: 'bg-pink-50 dark:bg-pink-900/20', border: 'border-pink-200 dark:border-pink-800', text: 'text-pink-600 dark:text-pink-400', btn: 'bg-pink-600 hover:bg-pink-700' },
  };
  const colors = colorClasses[accentColor] || colorClasses.blue;

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : `bg-gradient-to-br from-slate-50 to-${accentColor}-50`}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradientColors} flex items-center justify-center`}>
              <RefreshCw className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-800 dark:text-white">{sourceFormat} to {targetFormat}</h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">by KayTool</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <QuickConvert />
            <ToolsNav />
            <Badge variant="outline" className={`hidden sm:flex gap-1 ${colors.border} ${colors.text} ${colors.bg}`}>
              <RefreshCw className="w-3 h-3" /> {sourceFormat} → {targetFormat}
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="text-slate-600 dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Ad Placement - Above the Fold (Below Header) */}
      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-white mb-3">Convert {sourceFormat} to {targetFormat}</h2>
          <p className="text-slate-600 dark:text-slate-300 max-w-xl mx-auto">Drop your {sourceFormat} images and convert them to {targetFormat} format instantly. Free and private.</p>
        </div>

        {showQualitySlider && (
          <div className="flex justify-center mb-6">
            <Card className="p-4 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 w-full max-w-md">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Quality</span>
                <span className={`text-sm font-bold ${colors.text}`}>{quality[0]}%</span>
              </div>
              <Slider value={quality} onValueChange={setQuality} min={10} max={100} step={5} className="w-full" />
            </Card>
          </div>
        )}

        <div
          data-testid="upload-zone"
          className={`relative border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-300 cursor-pointer
            ${isDragging ? `border-${accentColor}-500 ${colors.bg} scale-[1.02]` : `border-slate-300 dark:border-slate-600 hover:border-${accentColor}-400 bg-white dark:bg-slate-800`}`}
          onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()}
        >
          <input ref={fileInputRef} type="file" accept={acceptTypes} multiple onChange={handleFileSelect} className="hidden" />
          <div className="flex flex-col items-center gap-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isDragging ? colors.bg : 'bg-slate-100 dark:bg-slate-700'}`}>
              <Upload className={`w-8 h-8 ${isDragging ? colors.text.split(' ')[0] : 'text-slate-400'}`} />
            </div>
            <div>
              <p className="text-lg font-medium text-slate-700 dark:text-slate-200">Drop, paste, or click to upload {sourceFormat} images</p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Ctrl+V to paste • Multiple files supported</p>
            </div>
            <Badge className={`${colors.bg} ${colors.text.replace('dark:', '').split(' ')[0]}`}>Convert to {targetFormat}</Badge>
          </div>
        </div>

        {isProcessing && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-600 dark:text-slate-300">Converting images...</span>
              <span className={`text-sm font-medium ${colors.text}`}>{compressionProgress}%</span>
            </div>
            <Progress value={compressionProgress} className="h-2" />
          </div>
        )}

        {images.length > 0 && (
          <div className="mt-8 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white">Converted Images ({images.length})</h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={clearAll} className="text-red-600 hover:bg-red-50"><X className="w-4 h-4 mr-1" /> Clear All</Button>
                {images.length > 1 && <Button size="sm" onClick={downloadAllAsZip} className={colors.btn}><Download className="w-4 h-4 mr-1" /> Download All (ZIP)</Button>}
              </div>
            </div>

            {/* Ad Placement - Above Results (336×280 - Best Money Position) */}
            <DesktopOnlyAd slot="above-results" size="large-rectangle" className="my-4" />

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {images.map((image) => (
                <Card key={image.id} className="overflow-hidden bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                  <div className="aspect-video relative bg-slate-100 dark:bg-slate-700">
                    <img src={image.preview} alt={image.name} className="w-full h-full object-contain" />
                    <button onClick={() => removeImage(image.id)} className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"><X className="w-4 h-4" /></button>
                    <Badge className={colors.btn.split(' ')[0]}>{targetFormat}</Badge>
                  </div>
                  <CardContent className="p-4">
                    <p className="font-medium text-slate-800 dark:text-white truncate text-sm">{image.name}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-500 dark:text-slate-400">
                      <span>{formatFileSize(image.originalSize)}</span><span>→</span>
                      <span className={`font-medium ${colors.text}`}>{formatFileSize(image.convertedSize)}</span>
                      <span className="ml-auto">{image.dimensions.width}×{image.dimensions.height}</span>
                    </div>
                    <Button onClick={() => downloadImage(image)} className={`w-full mt-3 ${colors.btn}`} size="sm"><Download className="w-4 h-4 mr-1" /> Download</Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <Card className="p-6 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <Zap className={`w-8 h-8 ${colors.text.split(' ')[0]} mb-3`} /><h3 className="font-semibold text-slate-800 dark:text-white">Instant</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Convert in seconds</p>
          </Card>
          <Card className="p-6 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <RefreshCw className={`w-8 h-8 ${colors.text.split(' ')[0]} mb-3`} /><h3 className="font-semibold text-slate-800 dark:text-white">High Quality</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Preserves image quality</p>
          </Card>
          <Card className="p-6 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <Info className={`w-8 h-8 ${colors.text.split(' ')[0]} mb-3`} /><h3 className="font-semibold text-slate-800 dark:text-white">100% Private</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Browser-only processing</p>
          </Card>
        </div>

        {/* Ad Placement - After Features (Responsive) */}
        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      {pageContent?.content && pageContent.content.length > 0 && (
        <div className="bg-slate-50 dark:bg-slate-800/50 py-12">
          <div className="max-w-6xl mx-auto px-4"><RenderCMSContent content={pageContent.content} /></div>
        </div>
      )}

      {/* Ad Placement - Before Footer (Responsive) */}
      <AdContainer className="my-6">
        <ResponsiveAd slot="before-footer" mobileSize="mobile-rectangle" desktopSize="rectangle" />
      </AdContainer>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-6 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-slate-500 dark:text-slate-400">
          <p>© 2025 KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      {/* Sticky Mobile Ad */}
      <StickyMobileAd slot="sticky-footer" />

      {/* Sticky Sidebar Ads - Desktop only */}
      <StickySidebar />
    </div>
  );
}
